import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UserPlus, HelpCircle } from "lucide-react";
import { auth } from "../firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import { useDarkMode } from "../DarkModeContext";
import Header from "../components/Header";
import TabBar from "../components/TabBar";

export default function HowTo() {
  const [user, setUser] = useState(null);
  const { darkMode } = useDarkMode();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  return (
    <div className={`relative min-h-screen overflow-x-hidden ${
      darkMode ? "text-gray-100" : "text-gray-800"
    }`}>
      {/* === FIXED BACKGROUND === */}
      <div className={`fixed inset-0 -z-10 ${
        darkMode
          ? "bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900"
          : ""
      }`} style={{
        background: darkMode
          ? undefined
          : 'linear-gradient(to bottom, #FFFADF 0%, rgba(255, 250, 223, 0.8) 50%, #FFFADF 100%)'
      }} />
      <div className="fixed inset-0 -z-10" style={{ background: 'radial-gradient(circle at 50% 20%, rgba(143, 179, 211, 0.12), transparent 60%)' }} />
      <div
        className="fixed inset-0 -z-10 opacity-[.04] pointer-events-none"
        style={{ backgroundImage: "url(/images/noise-512.png)" }}
      />

      {/* --- Shared Header --- */}
      <Header />

      {/* === MAIN CONTENT === */}
      <main
        className="relative z-10 max-w-2xl mx-auto px-6 pb-32"
        style={{
          paddingTop: 'max(5rem, calc(env(safe-area-inset-top, 0px) + 5rem))'
        }}
      >
        {/* Page Title */}
        <div className="mb-8">
          <h1 className={`text-3xl font-bold ${
            darkMode ? "text-gray-100" : "text-gray-900"
          }`} style={{ fontFamily: 'Rubik, sans-serif' }}>
            Getting Started
          </h1>
        </div>

        {/* Note */}
        {!user && (
          <div
            className={`mb-6 px-4 py-3 rounded-xl border ${
              darkMode ? "text-gray-300" : "text-gray-700"
            }`}
            style={{
              background: darkMode
                ? 'linear-gradient(to right, rgba(143, 179, 211, 0.2), rgba(56, 91, 55, 0.2))'
                : 'rgba(143, 179, 211, 0.15)',
              borderColor: darkMode ? '#8FB3D3' : '#8FB3D3'
            }}
          >
            <p className={`text-sm ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
              💡 Already purchased? Skip to <span className="font-semibold">Step 2</span>
            </p>
          </div>
        )}

        {/* Steps */}
        <div className="space-y-4">
          {/* Step 1 */}
          <div className={`rounded-2xl border shadow-sm hover:shadow-md transition-shadow p-6 ${
            darkMode
              ? "bg-gray-800 border-gray-700"
              : "bg-white border-gray-200"
          }`}>
            <div className="flex items-start gap-4">
              <div
                className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                style={{ background: 'linear-gradient(to bottom right, #F8662B, #F8662B)' }}
              >
                1
              </div>
              <div className="flex-1">
                <h3 className={`text-lg font-bold mb-2 ${
                  darkMode ? "text-gray-100" : "text-gray-900"
                }`} style={{ fontFamily: 'Rubik, sans-serif' }}>
                  Purchase your tour
                </h3>
                <p className={`text-sm leading-relaxed ${
                  darkMode ? "text-gray-300" : "text-gray-600"
                }`} style={{ fontFamily: 'Urbanist, sans-serif' }}>
                  Visit{" "}
                  <a
                    href="https://www.tourinabox.com/store"
                    target="_blank"
                    rel="noreferrer"
                    className="font-semibold underline underline-offset-2 hover:opacity-70"
                    style={{ color: '#F8662B' }}
                  >
                    tourinabox.com/store
                  </a>{" "}
                  to buy your tour. You'll receive a confirmation email.
                </p>
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className={`rounded-2xl border shadow-sm hover:shadow-md transition-shadow p-6 ${
            darkMode
              ? "bg-gray-800 border-gray-700"
              : "bg-white border-gray-200"
          }`}>
            <div className="flex items-start gap-4">
              <div
                className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                style={{ background: 'linear-gradient(to bottom right, #F8662B, #F8662B)' }}
              >
                2
              </div>
              <div className="flex-1">
                <h3 className={`text-lg font-bold mb-2 ${
                  darkMode ? "text-gray-100" : "text-gray-900"
                }`} style={{ fontFamily: 'Rubik, sans-serif' }}>
                  Create account & log in
                </h3>
                <p className={`text-sm leading-relaxed mb-3 ${
                  darkMode ? "text-gray-300" : "text-gray-600"
                }`} style={{ fontFamily: 'Urbanist, sans-serif' }}>
                  Sign up using the{" "}
                  <span className={`font-bold px-1.5 py-0.5 rounded ${
                    darkMode
                      ? "text-gray-100 bg-yellow-600/40"
                      : "text-gray-900 bg-yellow-100"
                  }`}>
                    same email
                  </span>{" "}
                  you used at checkout. This automatically unlocks your tours. If you already have an account, just log in.
                </p>
                {!user && (
                  <Link
                    to="/signup"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all hover:opacity-90"
                    style={{ backgroundColor: '#F8662B' }}
                  >
                    <UserPlus size={16} />
                    Create Account
                  </Link>
                )}
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div className={`rounded-2xl border shadow-sm hover:shadow-md transition-shadow p-6 ${
            darkMode
              ? "bg-gray-800 border-gray-700"
              : "bg-white border-gray-200"
          }`}>
            <div className="flex items-start gap-4">
              <div
                className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                style={{ background: 'linear-gradient(to bottom right, #F8662B, #F8662B)' }}
              >
                3
              </div>
              <div className="flex-1">
                <h3 className={`text-lg font-bold mb-2 ${
                  darkMode ? "text-gray-100" : "text-gray-900"
                }`} style={{ fontFamily: 'Rubik, sans-serif' }}>
                  Start exploring!
                </h3>
                <p className={`text-sm leading-relaxed ${
                  darkMode ? "text-gray-300" : "text-gray-600"
                }`} style={{ fontFamily: 'Urbanist, sans-serif' }}>
                  Head to the home page and tap your destination to begin your adventure.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Troubleshooting */}
        <div className={`mt-8 rounded-2xl border p-6 ${
          darkMode
            ? "bg-gradient-to-br from-gray-800 to-gray-700 border-gray-700"
            : "bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200"
        }`}>
          <div className="flex items-start gap-3">
            <div
              className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
              style={{ backgroundColor: darkMode ? 'rgba(143, 179, 211, 0.3)' : 'rgba(143, 179, 211, 0.2)' }}
            >
              <HelpCircle style={{ color: '#8FB3D3' }} size={18} />
            </div>
            <div>
              <h4 className={`text-base font-bold mb-3 ${
                darkMode ? "text-gray-100" : "text-gray-900"
              }`} style={{ fontFamily: 'Rubik, sans-serif' }}>
                Need Help?
              </h4>
              <ul className={`space-y-2 text-sm ${
                darkMode ? "text-gray-300" : "text-gray-700"
              }`}>
                <li className="flex items-start gap-2">
                  <span style={{ color: '#8FB3D3' }} className="mt-0.5">•</span>
                  <span>Make sure your account email matches the one used at checkout</span>
                </li>
                <li className="flex items-start gap-2">
                  <span style={{ color: '#8FB3D3' }} className="mt-0.5">•</span>
                  <span>Try signing out and back in if you're having issues</span>
                </li>
                <li className="flex items-start gap-2">
                  <span style={{ color: '#8FB3D3' }} className="mt-0.5">•</span>
                  <span>
                    Lost your gift certificate?{" "}
                    <Link
                      to="/gift-lookup"
                      className="font-semibold underline underline-offset-2 hover:opacity-70"
                      style={{ color: '#F8662B' }}
                    >
                      Find it here
                    </Link>
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span style={{ color: '#8FB3D3' }} className="mt-0.5">•</span>
                  <span>
                    Still stuck? Email us at{" "}
                    <a
                      href="mailto:hello@tourinabox.com"
                      className="font-semibold underline underline-offset-2 hover:opacity-70"
                      style={{ color: '#F8662B' }}
                    >
                      hello@tourinabox.com
                    </a>
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </main>

      {/* === FOOTER === */}
      <footer className={`relative z-10 text-center text-sm pb-28 ${
        darkMode ? "text-gray-400" : "text-gray-500"
      }`}>
        © {new Date().getFullYear()} Tour in a Box LLC
      </footer>

      {/* === Floating Nav === */}
      <TabBar isAuthed={!!user} />
    </div>
  );
}
